import { useCallback, useEffect, useRef, useState } from 'react';
import { Quill } from 'react-quill';
import '../../../communication/utils/quilMention.js';
import 'quill/dist/quill.snow.css'; // Import Quill stylesheet
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';

import {
  QUILL_API_ACTION_ADD_EMOJI,
  getUrlFromText,
  getUrlPreview,
  highlightFunctionAndQuillCopy,
  highlightQuillInput,
  setCursorPositionForQuill,
  getTextFromQuill,
  countAllCharacters
} from './config';
import {
  updatePostText,
  updateUrlPreviewData
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import axios from 'axios';
import { MENTION_TIMEOUT, TIMEOUT } from '../contants';
import { ALL } from '../../../../config/constants';
import './quillEditor.css';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator/index.js';
import Delta from 'quill-delta';
import { setCursorPosition } from '../../../../redux/slices/textBlocks/index';

import { registerHashtagBlot } from './hashtagBlot';

export const formats = [
  // "background",
  'bold',
  'color',
  'font',
  // "code",
  'italic',
  'link',
  'mention',
  // "size",
  'strike',
  // "script",
  'underline',
  // "blockquote",
  // "header",
  // "indent",
  // 'list',
  // "align",
  // "direction",
  // "code-block",
  // "formula",
  'image',
  // "video",
  'creatorHashtag'
];

const CreatorQuillEditor = ({
  platform,
  quillEditors,
  enabled,
  setQuillEditors
}) => {
  const locationDetails = useSelector((state: any) => state.locationDetails);
  const { lomavisCreatorConfig } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );
  const isEditorInitializing = useRef(false);
  const Quillref = useRef(null);
  const [quillEditor, setQuillEditor] = useState(null);
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const NOT_VERIFIED_TOOLTIP_TEXT = t('Not Verified');
  const VERIFIED_TOOLTIP_TEXT = t('Verified');
  let timeoutId;
  let controller;
  const {
    platformEnabled,
    postData: { [platform]: platformPostData }
  } = useSelector((state: any) => state.lomavisCreator);

  const getMentionsListAxios = async (
    platform,
    searchTerm,
    signal,
    lomavisCreatorConfig
  ) => {
    try {
      const urls =
        platform === ALL
          ? Object.values(lomavisCreatorConfig?.mention_search_urls)
          : [lomavisCreatorConfig?.mention_search_urls?.[platform]];
      const responses = await Promise.allSettled(
        urls.map(
          async (url) =>
            //@ts-ignore
            await axios.get(url, {
              signal,
              params: {
                q: searchTerm
              }
            })
        )
      );

      // Process each response based on its status
      const results = responses.flatMap((response, index) => {
        if (response.status === 'fulfilled') {
          return response.value.data;
        } else {
          console.error(
            `Error fetching data from ${urls[index]}:`,
            response.reason
          );
          return []; // Return an empty array for failed requests
        }
      });

      // Return the aggregated results
      return results;
    } catch (error) {
      console.error('Error in getMentionsListAxios:', error);
      return [];
    }
  };

  const getMention = () => {
    return {
      allowedChars:
        /^[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]*(?:\s[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]+)*$/i,
      mentionDenotationChars: ['@'],
      minChars: 1,
      maxChars: 180,
      isolateCharacter: true,
      showDenotationChar: true,
      spaceAfterInsert: true,
      mentionContainerClass: 'z-3 ',
      mentionListStyle: {
        zIndex: 1000
      },
      mentionListClass: 'bg-light-secondary rounded list-unstyled z-3',
      listItemClass: '  ',
      dataAttributes: [
        'id',
        'display_name',
        'denotationChar',
        'link',
        'target',
        'disabled',
        'image_url',
        'iconClass',
        'platform_identifier',
        'platform',
        'platform_css_class',
        'show_denotation_char',
        'is_verified'
      ],
      mentionListStyleOptions:
        'max-height:25em; overflow-y:scroll; margin:0; padding:0;',
      mentionContainerClassOptions: [
        'rounded',
        'bg-light-secondary',
        'mention-top',
        'mention-container-selector' // used to select the mention container
      ],
      mentionContainerStyleOptions:
        'padding: 5px 3px; position: absolute; visibility: visible; top: 31px; left: 16px; z-index:9999;',
      renderItem: function (item) {
        let iconColorClass = 'text-warning';
        let verifiedText = t('lomavisCreator.Quill.Mention.NotVerified');
        let verificationTooltip = t(
          'lomavisCreator.Quill.Mention.notVerifiedToolTip'
        );
        if (item.is_verified) {
          iconColorClass = 'text-success';
          verifiedText = t('lomavisCreator.Quill.Mention.verified');
          verificationTooltip = t(
            'lomavisCreator.Quill.Mention.verifiedToolTip'
          );
        }
        const element = `<div class="dark d-flex flex-row align-items-center bg-white rounded py-1 px-1 mt-2 mx-2 cursor-pointer"><img src="${item.image_url}" class="w-50px h-50px"><div class="ms-1 d-flex flex-column"><div class="d-flex fw-bolder" style="white-space:pre-wrap;">${item.display_name}</div><div class="w-100"><small data-bs-toggle-old="tooltip" data-bs-placement="top" data-bs-title="${verificationTooltip}"><i class="fas fa-check-circle fa-1x ${iconColorClass} px-2"></i> ${verifiedText}</small></div></div><div class="d-flex flex-grow-1 justify-content-end"><i class="fab ${item.platform_css_class} ms-1 text-primary"></i></div></div>`;
        return element;
      },
      renderLoading: function () {
        // remove the class min-w-300px from the mention list
        const mentionList = document.querySelector(
          '.mention-container-selector'
        );
        mentionList?.classList?.remove('min-w-300px');
        return '<div class="d-flex"><div class="spinner-border text-primary spinner-border-sm" role="status"></div></div>';
      },
      source: async function (searchTerm, renderList, mentionChar) {
        // Clear the previous timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Cancel the previous request if it exists
        if (controller) {
          controller.abort();
        }

        // Create a new AbortController instance
        controller = new AbortController();

        // Set a new timeout
        timeoutId = setTimeout(async () => {
          try {
            const mentions = await getMentionsListAxios(
              platform,
              searchTerm,
              controller.signal,
              lomavisCreatorConfig
            );

            // add a class w-300 to the mention list
            const mentionList = document.querySelector(
              '.mention-container-selector'
            );
            mentionList.classList.add('min-w-300px');
            // set max height css property to the mention list as the quill editor container width
            const quillContainer = document.querySelector('.ql-container');
            mentionList.setAttribute(
              'style',
              `max-width: ${quillContainer.clientWidth}px;`
            );
            renderList(mentions, searchTerm);
          } catch (error) {
            console.log('Error in source:', error);
          }
        }, MENTION_TIMEOUT);
      }
    };
  };

  useEffect(() => {
    console.warn('i am triggered');
    registerHashtagBlot(); // Only called once during app startup
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Check if an editor already exists
    // if (Quillref.current?.__quill) {
    //   console.warn(
    //     'Quill editor already initialized. Skipping reinitialization.'
    //   );
    //   return;
    // }

    let editor: any; // Declare editor here to access it in the cleanup function

    const clearToolbar = () => {
      const quillInstance = Quillref.current?.__quill;

      if (quillInstance) {
        // Remove Quill event listeners
        quillInstance.off('text-change');
        quillInstance.off('selection-change');

        // Manually destroy any observers if present (like in mention module)
        if (quillInstance?.destroy) quillInstance.destroy();
      }

      if (Quillref.current) {
        // Remove toolbar manually
        const toolbar =
          Quillref.current.parentElement?.querySelector('.ql-toolbar');
        if (toolbar) toolbar.remove();

        // Reset the container
        Quillref.current.innerHTML = '';
        delete Quillref.current.__quill;
      }

      setQuillEditor(null);
    };

    // remove the instance of the editor and add a new one

    const initializeEditor = async (): Promise<void> => {
      const modules = {
        toolbar: [['bold', 'italic']],
        mention: getMention() // If getMention returns a promise, you might need to await it as well
      };

      editor = new Quill(Quillref.current, {
        theme: 'snow',
        readOnly: false,
        formats,
        modules
      });

      const exclusiveFormats = ['bold', 'italic'];

      // Override toolbar button behavior
      const toolbar = editor.getModule('toolbar');

      toolbar.container.addEventListener('click', (e: any) => {
        const target = e.target.closest('button');
        if (!target) return;

        const format = target.classList.value
          .split(' ')
          .find((cls) => cls.startsWith('ql-'))
          ?.replace('ql-', '');

        if (!format || !exclusiveFormats.includes(format)) return;

        const range = editor.getSelection();
        if (!range) return;

        const currentFormats = editor.getFormat(range);

        // Apply the clicked format if it is active and remove other formats
        exclusiveFormats.forEach((f) => {
          if (format === f && currentFormats[f]) {
            editor.format(f, true);
          } else {
            editor.format(f, false);
          }
        });

        e.preventDefault(); // Prevent default Quill toggle
      });

      editor.container.dataset.platform = platform;

      // Set the initial height of the editor's content area
      const editorElement = editor.root;
      editorElement.style.height = '150px';

      setQuillEditor(editor);
      dispatch(setQuillEditors({ platform, editor }));

      if (platformPostData?.text) {
        editor.setContents(platformPostData.text);

        highlightQuillInput(editor);

        const text = editor.getText();
        const textWithMentions = getTextFromQuill(editor);
        dispatch(
          updatePostText({
            text: platformPostData.text,
            text_as_html: editor.root.innerHTML,
            platform,
            textText: textWithMentions,
            urlPreview: null,
            url_for_linkpreview: getUrlFromText(text),
            fullCharacterCount: countAllCharacters(editor)
          })
        );

        // run async function to get url preview
        getUrlPreview(text)
          .then((urlPrev) => {
            dispatch(
              updateUrlPreviewData({
                platform,
                data: urlPrev,
                url_for_linkpreview: getUrlFromText(text)
              })
            );
          })
          .catch((error) => {
            console.error('Error in getUrlPreview:', error);
          });
      }
    };

    clearToolbar();
    initializeEditor()
      .then(() => console.log('Editor initialized', platform))
      .catch((error) => {
        console.error('Error in initializeEditor:', error);
      });

    // Cleanup function
    return () => {
      if (editor) {
        editor.off('text-change');
        editor.off('selection-change');
        if (editor?.destroy) editor.destroy();
      }
      const toolbar =
        Quillref.current?.parentElement?.querySelector('.ql-toolbar');
      if (toolbar) toolbar.remove();

      if (Quillref.current) {
        Quillref.current.innerHTML = '';
      }
    };
  }, [platformEnabled, locationDetails]);

  useEffect(() => {
    if (!quillEditor) {
      return;
    }
    // add listener for text change for platforms
    if (platform !== ALL && enabled) {
      quillEditor.on('text-change', function (delta, oldDelta, source) {
        let markHashtagTimeout = null;
        const actionTriggeredByUser =
          source === 'user' || source === QUILL_API_ACTION_ADD_EMOJI;
        if (actionTriggeredByUser) {
          // setCursorPositionForQuill(quillInstance)

          if (markHashtagTimeout) {
            clearTimeout(markHashtagTimeout);
            markHashtagTimeout = undefined;
          }

          markHashtagTimeout = setTimeout(function () {
            highlightQuillInput(quillEditor);
            const text = quillEditor.getText();
            const textWithMentions = getTextFromQuill(quillEditor);
            const sanitizedHtml = quillEditor.root.innerHTML.replace(
              /<(strong|em|u|s)(\s[^>]*)?>(.*?)<\/\1>/g,
              '<span$2>$3</span>'
            );

            dispatch(
              updatePostText({
                text: quillEditor.getContents(),
                text_as_html: sanitizedHtml,
                platform,
                textText: textWithMentions,
                urlPreview: null,
                url_for_linkpreview: getUrlFromText(text),
                fullCharacterCount: countAllCharacters(quillEditor)
              })
            );
            getUrlPreview(text)
              .then((urlPrev) => {
                // update text on the slice of the text

                dispatch(
                  updateUrlPreviewData({
                    platform,
                    data: urlPrev,
                    url_for_linkpreview: getUrlFromText(text)
                  })
                );
              })
              .catch((error) => {
                console.error('Error in getUrlPreview:', error);
              });
          }, TIMEOUT);
        }
      });
    }

    if (platform === ALL) {
      let markHashtagTimeoutAllPlatformsText;
      quillEditor.on('text-change', function (delta, oldDelta, source) {
        console.log('action triggered by who?', source, delta);
        const actionTriggeredByUser =
          source === 'user' || source === QUILL_API_ACTION_ADD_EMOJI;

        if (actionTriggeredByUser) {
          setCursorPositionForQuill(quillEditor);
          if (markHashtagTimeoutAllPlatformsText) {
            clearTimeout(markHashtagTimeoutAllPlatformsText);
            markHashtagTimeoutAllPlatformsText = undefined;
          }
          const allQuillsButCurrent = Object.values(quillEditors).filter(
            (editor) => editor !== quillEditor
          );
          markHashtagTimeoutAllPlatformsText = setTimeout(() => {
            highlightFunctionAndQuillCopy(quillEditor, allQuillsButCurrent);
            // copy changes to
            // update text on the slice of the text
            const text = quillEditor.getText();
            const textWithMentions = getTextFromQuill(quillEditor);
            const sanitizedHtml = quillEditor.root.innerHTML.replace(
              /<(strong|em|u|s)(\s[^>]*)?>(.*?)<\/\1>/g,
              '<span$2>$3</span>'
            );
            dispatch(
              updatePostText({
                text: quillEditor.getContents(),
                text_as_html: sanitizedHtml,
                textText: textWithMentions,
                platform: ALL,
                urlPreview: null,
                fullCharacterCount: countAllCharacters(quillEditor)
              })
            );
            getUrlPreview(text)
              .then((urlPrev) => {
                dispatch(
                  updateUrlPreviewData({
                    platform: ALL,
                    data: urlPrev,
                    url_for_linkpreview: getUrlFromText(text)
                  })
                );
              })
              .catch((error) => {
                console.error('Error in getUrlPreview:', error);
              });
          }, TIMEOUT);
        }
      });
    }

    quillEditor.on('selection-change', function (range, oldRange, source) {
      const actionTriggeredByUser = source === 'copyPaste';
      if (actionTriggeredByUser) {
        setCursorPositionForQuill(quillEditor);
      }
    });

    return () => {
      quillEditor.off('text-change');
      quillEditor.off('selection-change');
    };
  }, [quillEditor]);

  useEffect(() => {
    if (!quillEditor) {
      return;
    }
    quillEditor.on('selection-change', function (range, oldRange, source) {
      const isLosingFocus = range === null && oldRange != null;

      // Save cursor position before blur
      if (isLosingFocus && source === 'user') {
        const lastCursorIndex = oldRange.index;
        dispatch(setCursorPosition({ platform, index: lastCursorIndex }));
      }

      // Optional: if you also want to track copyPaste selection
      const actionTriggeredByUser = source === 'copyPaste';
      if (actionTriggeredByUser) {
        setCursorPositionForQuill(quillEditor);
      }
    });
  }, [quillEditor]);

  return (
    <div
      className="custom-editor overflow-visible w-100  border rounded ql-container"
      ref={Quillref}
      style={{
        minHeight: 150
      }}
    ></div>
  );
};

export default CreatorQuillEditor;
